import { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { X, Plus, Trash2, Eye, EyeOff, User, Mail, Phone, Building2, Shield, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { userManagementV2Service, UserV2, UserV2CreateRequest } from '@/services/userManagementV2.service';
import { FileUploadField, InputField } from '@/components/common/form-fields';
import { Select, SelectOption } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { generateRequestId, encryptAES } from '@/utils/crypto';
import { isValidPhoneNumber } from 'react-phone-number-input';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';

interface ActiveUser {
  _id: string;
  employee_id: string;
  full_name: string;
  email: string;
}

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingUser?: UserV2 | null;
  availableGroups: Array<{
    group_key: string;
    group_name: string;
    roles: Array<{ role_key: string; role_name: string; _id?: string }>;
  }>;
}

type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'LOCKED' | 'ARCHIVED' | 'DELETED';

interface UserFormData {
  email: string;
  given_name: string;
  family_name: string;
  password: string;
  personal_email: string;
  employee_id: string;
  mobile_number: string;
  profile_image: string;
  status: UserStatus;
  suspension_reason?: string;
  archive_reason?: string;
  groups: Array<{
    group_key: string;
    group_name: string;
    roles: Array<{
      role_key: string;
      role_name: string;
    }>;
  }>;
  meta_data: Record<string, any>;
  /** Additional Metadata rows, edited through the shared Input */
  meta_fields: Array<{ key: string; value: string }>;
  linked_social_accounts: Array<{
    provider: string;
    account_id: string;
    email?: string;
  }>;
  // Organizational Hierarchy (optional)
  reporting_manager_email?: string;
  bu_head_email?: string;
  hrbp_email?: string;
}

export default function UserFormModal({
  isOpen,
  onClose,
  onSuccess,
  editingUser,
  availableGroups
}: UserFormModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const isEditMode = !!editingUser;

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    register
  } = useForm<UserFormData>({
    mode: 'onChange', // Validate on change to clear errors when user types
    defaultValues: {
      email: '',
      given_name: '',
      family_name: '',
      password: '',
      personal_email: '',
      employee_id: '',
      mobile_number: '',
      profile_image: '',
      status: 'ACTIVE',
      suspension_reason: '',
      archive_reason: '',
      groups: [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }],
      meta_data: {},
      meta_fields: [{ key: '', value: '' }],
      linked_social_accounts: [],
      reporting_manager_email: '',
      bu_head_email: '',
      hrbp_email: ''
    }
  });

  // Watch form values
  const formValues = watch();

  // Field array for groups
  const { fields: groupFields, append: appendGroup, remove: removeGroup } = useFieldArray({
    control,
    name: 'groups'
  });

  // Field array for Additional Metadata rows
  const { fields: metaFields, append: appendMetaField, remove: removeMetaField } = useFieldArray({
    control,
    name: 'meta_fields'
  });

  // Fetch active users for reporting dropdowns
  useEffect(() => {
    const fetchActiveUsers = async () => {
      if (!isOpen) return;

      setIsLoadingUsers(true);
      try {
        const response = await apiClient.get(API_ENDPOINTS.USERS.ACTIVE_USERS_FOR_REPORTING);
        if (response.data?.success && response.data?.data) {
          setActiveUsers(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching active users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchActiveUsers();
  }, [isOpen]);

  // Active users options for SimpleSelect
  const activeUserOptions = activeUsers.map(user => ({
    label: user.employee_id
      ? `${user.full_name} (${user.employee_id}) ${user.email}`
      : `${user.full_name} ${user.email}`,
    value: user.email
  }));

  // Watch for group and role changes to set names
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      // Watch for group_key changes to set group_name
      if (name?.includes('group_key') && type === 'change') {
        const match = name.match(/groups\.(\d+)\.group_key/);
        if (match) {
          const groupIndex = parseInt(match[1]);
          const groupKey = value.groups?.[groupIndex]?.group_key;
          if (groupKey) {
            const selectedGroup = availableGroups.find(g => g.group_key === groupKey);
            if (selectedGroup) {
              setValue(`groups.${groupIndex}.group_name`, selectedGroup.group_name);
              // Reset roles when department changes
              setValue(`groups.${groupIndex}.roles`, [{ role_key: '', role_name: '' }]);
            }
          }
        }
      }
      // Watch for role_key changes to set role_name
      if (name?.includes('role_key') && type === 'change') {
        const match = name.match(/groups\.(\d+)\.roles\.(\d+)\.role_key/);
        if (match) {
          const groupIndex = parseInt(match[1]);
          const roleIndex = parseInt(match[2]);
          const groupKey = value.groups?.[groupIndex]?.group_key;
          const roleKey = value.groups?.[groupIndex]?.roles?.[roleIndex]?.role_key;
          if (groupKey && roleKey) {
            const selectedGroup = availableGroups.find(g => g.group_key === groupKey);
            const selectedRole = selectedGroup?.roles?.find(r => r.role_key === roleKey);
            if (selectedRole) {
              setValue(`groups.${groupIndex}.roles.${roleIndex}.role_name`, selectedRole.role_name);
            }
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, availableGroups, setValue]);

  // Load user data when editing
  useEffect(() => {
    if (editingUser) {
      reset({
        email: editingUser.email || '',
        given_name: editingUser.given_name || '',
        family_name: editingUser.family_name || '',
        password: '', // Don't populate password when editing
        personal_email: editingUser.personal_email || '',
        employee_id: editingUser.employee_id || '',
        mobile_number: editingUser.mobile_number || '',
        profile_image: editingUser.profile_image || '',
        status: (editingUser as any).status || 'ACTIVE',
        suspension_reason: (editingUser as any).status_metadata?.suspension_reason || '',
        archive_reason: (editingUser as any).status_metadata?.archive_reason || '',
        groups: editingUser.groups && editingUser.groups.length > 0
          ? editingUser.groups
          : [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }],
        meta_data: (editingUser as any).meta_data || {},
        // Load meta_data into the metadata rows for editing
        meta_fields: Object.keys((editingUser as any).meta_data || {}).length > 0
          ? Object.entries((editingUser as any).meta_data).map(([key, value]) => ({ key, value: String(value) }))
          : [{ key: '', value: '' }],
        linked_social_accounts: (editingUser as any).linked_social_accounts || [],
        reporting_manager_email: (editingUser as any).reporting_manager_email || '',
        bu_head_email: (editingUser as any).bu_head_email || '',
        hrbp_email: (editingUser as any).hrbp_email || ''
      });
    } else {
      reset({
        email: '',
        given_name: '',
        family_name: '',
        password: '',
        personal_email: '',
        employee_id: '',
        mobile_number: '',
        profile_image: '',
        status: 'ACTIVE',
        suspension_reason: '',
        archive_reason: '',
        groups: [{ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] }],
        meta_data: {},
        meta_fields: [{ key: '', value: '' }],
        linked_social_accounts: [],
        reporting_manager_email: '',
        bu_head_email: '',
        hrbp_email: ''
      });
    }
  }, [editingUser, reset]);

  // Group options for Select
  const groupOptions: SelectOption[] = availableGroups.map(group => ({
    label: group.group_name,
    value: group.group_key
  }));

  // Status options for Select with descriptions
  const statusOptions: SelectOption[] = [
    { label: '🟢 Active - Full access', value: 'ACTIVE' },
    { label: '🟡 Inactive - Temporarily disabled', value: 'INACTIVE' },
    { label: '🔴 Suspended - Blocked (policy violation)', value: 'SUSPENDED' },
    { label: '🔒 Locked - Security lockout', value: 'LOCKED' },
    { label: '📦 Archived - Offboarded employee', value: 'ARCHIVED' },
    { label: '🗑️ Deleted - Marked for deletion', value: 'DELETED' }
  ];

  // Get roles for a specific group
  const getRolesForGroup = (groupKey: string) => {
    if (!groupKey) return [];
    const selectedGroup = availableGroups.find(g => g.group_key === groupKey);
    return selectedGroup?.roles || [];
  };

  // Handle group selection change
  const handleGroupChange = (groupIndex: number, groupKey: string) => {
    const selectedGroup = availableGroups.find(g => g.group_key === groupKey);
    if (selectedGroup) {
      setValue(`groups.${groupIndex}.group_key`, groupKey);
      setValue(`groups.${groupIndex}.group_name`, selectedGroup.group_name);
      // Reset roles when department changes
      setValue(`groups.${groupIndex}.roles`, [{ role_key: '', role_name: '' }]);
    }
  };

  // Handle role selection change
  const handleRoleChange = (groupIndex: number, roleIndex: number, roleKey: string) => {
    const groupKey = formValues.groups[groupIndex]?.group_key;
    const rolesForGroup = getRolesForGroup(groupKey);
    const selectedRole = rolesForGroup.find(r => r.role_key === roleKey);
    if (selectedRole) {
      setValue(`groups.${groupIndex}.roles.${roleIndex}.role_key`, roleKey);
      setValue(`groups.${groupIndex}.roles.${roleIndex}.role_name`, selectedRole.role_name);
    }
  };

  // Add role to a group
  const addRole = (groupIndex: number) => {
    const currentRoles = formValues.groups[groupIndex].roles;
    setValue(`groups.${groupIndex}.roles`, [
      ...currentRoles,
      { role_key: '', role_name: '' }
    ]);
  };

  // Remove role from a group
  const removeRole = (groupIndex: number, roleIndex: number) => {
    const currentRoles = formValues.groups[groupIndex].roles;
    if (currentRoles.length > 1) {
      setValue(
        `groups.${groupIndex}.roles`,
        currentRoles.filter((_, index) => index !== roleIndex)
      );
    }
  };

  // Form submission
  const onSubmit = async (data: UserFormData) => {
    // Validation: Check that each group has at least one role
    for (let i = 0; i < data.groups.length; i++) {
      const group = data.groups[i];

      if (!group.group_key) {
        toast.error(`Department ${i + 1}: Please select a department`);
        return;
      }

      if (group.roles.length === 0 || !group.roles[0].role_key) {
        toast.error(`Department ${i + 1}: At least one role must be assigned`);
        return;
      }

      // Validate that all roles have a role_key selected
      for (let j = 0; j < group.roles.length; j++) {
        if (!group.roles[j].role_key) {
          toast.error(`Department ${i + 1}, Role ${j + 1}: Please select a role`);
          return;
        }
      }
    }

    // Convert metadata rows to meta_data object (the shared Input already ran the clean-text rule)
    const { meta_fields, ...form_data } = data;
    const meta_data: Record<string, any> = {};
    meta_fields.forEach(field => {
      if (field.key.trim()) {
        meta_data[field.key.trim()] = field.value;
      }
    });

    // Build status_metadata if status is SUSPENDED or ARCHIVED
    const status_metadata: any = {};
    if (data.status === 'SUSPENDED' && data.suspension_reason) {
      status_metadata.suspended_at = new Date();
      status_metadata.suspension_reason = data.suspension_reason;
    }
    if (data.status === 'ARCHIVED' && data.archive_reason) {
      status_metadata.archived_at = new Date();
      status_metadata.archive_reason = data.archive_reason;
    }

    // Generate request_id for encryption
    const requestId = generateRequestId();

    // Encrypt sensitive fields
    const encryptedEmail = encryptAES(data.email, requestId);
    const encryptedPassword = data.password ? encryptAES(data.password, requestId) : undefined;

    // Prepare submission data with encrypted fields
    // Mobile number is optional - send empty when only the country code is present
    const mobileDigits = data.mobile_number?.replace(/\D/g, '') || '';
    const mobile_number = mobileDigits.length > 3 ? data.mobile_number : '';

    const submissionData = {
      ...form_data,
      mobile_number,
      request_id: requestId,
      email: encryptedEmail,
      ...(encryptedPassword && { password: encryptedPassword }),
      meta_data,
      ...(Object.keys(status_metadata).length > 0 && { status_metadata })
    };

    setIsSaving(true);
    try {
      if (isEditMode && editingUser) {
        // Update existing user
        await userManagementV2Service.updateUser(editingUser._id, submissionData as any);
        toast.success('Employee updated successfully!');
      } else {
        // Create new user
        await userManagementV2Service.createUser(submissionData as UserV2CreateRequest);
        toast.success('Employee added successfully!');
        // Reset form after successful creation
        reset();
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving user:', error);
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} user`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 common-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white rounded-t-xl border-b px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {isEditMode ? 'Edit Employee' : 'Add New Employee'}
                </h2>
                <p className="text-sm text-gray-500">
                  {isEditMode ? 'Update employee information' : 'Create a new employee account'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="user-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input<UserFormData>
                  name="given_name"
                  control={control}
                  label="First Name"
                  required
                  placeholder="Enter first name"
                  rules={{
                    required: 'First name is required',
                    minLength: { value: 2, message: 'First name must be at least 2 characters' }
                  }}
                />

                <Input<UserFormData>
                  name="family_name"
                  control={control}
                  label="Last Name"
                  required
                  placeholder="Enter last name"
                  rules={{
                    required: 'Last name is required',
                    minLength: { value: 1, message: 'Last name must be at least 1 character' }
                  }}
                />

                <InputField
                  label="Email"
                  required
                  fieldType="email"
                  error={errors.email?.message}
                  placeholder="employee@company.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />

                {!isEditMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' }
                        })}
                        className={`w-full input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                        placeholder="Enter password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
                    )}
                  </div>
                )}

                <Controller
                  name="mobile_number"
                  control={control}
                  rules={{
                    // Optional field - only validate when a number has been entered
                    validate: (value) => {
                      if (!value || value.trim() === '') return true;
                      // Get digits only to check if user has started entering
                      const digitsOnly = value?.replace(/\D/g, '') || '';
                      // Only the country code is present - treat as empty
                      if (digitsOnly.length <= 3) return true;
                      // Use library's built-in validation for proper country-specific validation
                      return isValidPhoneNumber(value) || 'Please enter a valid mobile number';
                    }
                  }}
                  render={({ field }) => (
                    <InputField
                      ref={field.ref}
                      label="Mobile Number"
                      fieldType="phone"
                      error={errors.mobile_number?.message}
                      placeholder="+1 234 567 8900"
                      phoneValue={field.value}
                      onPhoneChange={(phone) => field.onChange(phone)}
                      phoneCountry="in"
                    />
                  )}
                />

                <InputField
                  label="Personal Email"
                  fieldType="email"
                  error={errors.personal_email?.message}
                  placeholder="personal@email.com"
                  {...register('personal_email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />

                <Input<UserFormData>
                  name="employee_id"
                  control={control}
                  label="Employee ID"
                  placeholder="EMP-001"
                />

                {isEditMode && (
                  <Select
                    name="status"
                    control={control}
                    label="Account Status"
                    options={statusOptions}
                    placeholder="Select status"
                  />
                )}

                {/* Suspension Reason - Show only when status is SUSPENDED */}
                {isEditMode && formValues.status === 'SUSPENDED' && (
                  <div className="md:col-span-2">
                    <Textarea
                      name="suspension_reason"
                      control={control}
                      label="Suspension Reason"
                      required
                      error={errors.suspension_reason?.message}
                      rows={3}
                      placeholder="Enter reason for suspension (e.g., policy violation, code of conduct breach)"
                      rules={{
                        required: formValues.status === 'SUSPENDED' ? 'Suspension reason is required' : false
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">This reason will be shown to the user when they try to login.</p>
                  </div>
                )}

                {/* Archive Reason - Show only when status is ARCHIVED */}
                {isEditMode && formValues.status === 'ARCHIVED' && (
                  <div className="md:col-span-2">
                    <Textarea
                      name="archive_reason"
                      control={control}
                      label="Archive Reason"
                      error={errors.archive_reason?.message}
                      rows={2}
                      placeholder="Enter reason for archiving (e.g., resignation, retirement, contract end)"
                    />
                  </div>
                )}

                <div className={isEditMode ? "md:col-span-2" : "md:col-span-2"}>
                  <FileUploadField
                    label="Profile Photo"
                    description="Upload a profile photo for the employee"
                    currentFileUrl={formValues.profile_image || undefined}
                    onFileChange={(url) => setValue('profile_image', url || '')}
                    accept="image/*"
                    fileType="image"
                    maxSizeMB={2}
                    metadata={{ category: 'user', type: 'profile-image' }}
                  />
                </div>
              </div>
            </div>

            {/* Department & Roles */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Department & Roles
                  <span className="text-red-500">*</span>
                </h3>
                <button
                  type="button"
                  onClick={() => appendGroup({ group_key: '', group_name: '', roles: [{ role_key: '', role_name: '' }] })}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Department
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                At least one department assignment is required. Select departments and assign roles.
              </p>

              <div className="space-y-4">
                {groupFields.map((group, groupIndex) => (
                  <div key={group.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-600" />
                        Department {groupIndex + 1}
                      </h4>
                      {groupFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeGroup(groupIndex)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Department Selection */}
                    <div className="mb-4">
                      <Select
                        name={`groups.${groupIndex}.group_key` as any}
                        control={control}
                        label="Select Department"
                        options={groupOptions}
                        placeholder="Choose a department"
                        rules={{ required: 'Department is required' }}
                      />
                    </div>

                    {/* Roles for this department */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-gray-600" />
                          Roles <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const currentRoles = formValues.groups[groupIndex].roles;
                            setValue(`groups.${groupIndex}.roles`, [
                              ...currentRoles,
                              { role_key: '', role_name: '' }
                            ]);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add Role
                        </button>
                      </div>

                      <div className="space-y-2">
                        {formValues.groups[groupIndex]?.roles.map((role, roleIndex) => {
                          // Get roles for the selected department
                          const groupKey = formValues.groups[groupIndex]?.group_key;
                          const rolesForGroup = getRolesForGroup(groupKey);
                          const roleOptions: SelectOption[] = rolesForGroup.map(r => ({
                            label: `${r.role_name} (${r.role_key})`,
                            value: r.role_key
                          }));

                          return (
                            <div key={roleIndex} className="flex items-center gap-2">
                              <div className="flex-1">
                                <Select
                                  name={`groups.${groupIndex}.roles.${roleIndex}.role_key` as any}
                                  control={control}
                                  options={roleOptions}
                                  placeholder={groupKey ? "Select role" : "Select department first"}
                                  rules={{ required: 'Role is required' }}
                                  isDisabled={!groupKey}
                                />
                              </div>
                              {formValues.groups[groupIndex].roles.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeRole(groupIndex, roleIndex)}
                                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Organizational Hierarchy (Optional) */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Organizational Hierarchy
                <span className="text-sm font-normal text-gray-500">(Optional)</span>
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Assign reporting structure for this employee.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  name="bu_head_email"
                  control={control}
                  render={({ field }) => (
                    <SimpleSelect
                      label="BU Head"
                      value={field.value || ''}
                      onChange={(value) => field.onChange(value || '')}
                      options={activeUserOptions}
                      placeholder={isLoadingUsers ? "Loading..." : "Select BU Head"}
                      isSearchable={true}
                      isClearable
                      isDisabled={isLoadingUsers}
                    />
                  )}
                />
                <Controller
                  name="reporting_manager_email"
                  control={control}
                  render={({ field }) => (
                    <SimpleSelect
                      label="Reporting Manager"
                      value={field.value || ''}
                      onChange={(value) => field.onChange(value || '')}
                      options={activeUserOptions}
                      placeholder={isLoadingUsers ? "Loading..." : "Select manager"}
                      isSearchable={true}
                      isClearable
                      isDisabled={isLoadingUsers}
                    />
                  )}
                />
                
                <Controller
                  name="hrbp_email"
                  control={control}
                  render={({ field }) => (
                    <SimpleSelect
                      label="HRBP"
                      value={field.value || ''}
                      onChange={(value) => field.onChange(value || '')}
                      options={activeUserOptions}
                      placeholder={isLoadingUsers ? "Loading..." : "Select HRBP"}
                      isSearchable={true}
                      isClearable
                      isDisabled={isLoadingUsers}
                    />
                  )}
                />
              </div>
            </div>

            {/* Additional Metadata (Optional) */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Additional Metadata</h3>
                  <p className="text-sm text-gray-600 mt-1">Add custom fields like department, location, hire date, etc.</p>
                </div>
                <button
                  type="button"
                  onClick={() => appendMetaField({ key: '', value: '' })}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              <div className="space-y-3">
                {metaFields.map((field, index) => (
                  <div key={field.id} className="grid grid-cols-2 gap-3 items-start">
                    <Input<UserFormData>
                      name={`meta_fields.${index}.key`}
                      control={control}
                      placeholder="Field name (e.g., department)"
                    />
                    <div className="flex gap-2 items-start">
                      <Input<UserFormData>
                        name={`meta_fields.${index}.value`}
                        control={control}
                        placeholder="Value (e.g., Engineering)"
                      />
                      {metaFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMetaField(index)}
                          className="p-2 mt-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {metaFields.length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    No metadata fields added yet
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-white border-t px-6 py-4 rounded-b-xl">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {isEditMode ? 'Update employee information' : 'All fields marked with * are required'}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-form"
                disabled={isSaving}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 font-medium"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4" />
                    <span>{isEditMode ? 'Update Employee' : 'Create Employee'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
